/**
 * Helpers de estilo del Hero Zone del micrositio.
 *
 * Viven aca y no dentro de RematePage porque tambien los usa el editor del
 * panel (Herramientas/components/FondoHeroTab): importarlos desde RematePage
 * arrastraria todo el micrositio publico al bundle del admin.
 *
 * RematePage los re-exporta para no romper los imports que ya existian.
 */

// Overlay del hero: color + opacidad editables por el productor (config).
// Default rgba(0,0,0,0.25) — el valor histórico fijo. Devuelve el string rgba.
export const heroOverlayRgba = (config = {}) => {
  const hex = (config.hero_overlay_color || '#000000').replace('#', '');
  const op = config.hero_overlay_opacity != null ? Number(config.hero_overlay_opacity) : 0.25;
  const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
  const r = parseInt(full.substring(0, 2), 16) || 0;
  const g = parseInt(full.substring(2, 4), 16) || 0;
  const b = parseInt(full.substring(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${isNaN(op) ? 0.25 : op})`;
};

// ── Encuadre de la imagen de fondo del hero ─────────────────────────────────
// Los defaults reproducen el "center/cover" que estaba hardcodeado, asi que un
// evento sin estos valores en su config se ve exactamente igual que antes.

// background-position: "50% 50%" por default.
export const heroBgPosition = (config = {}) => {
  const x = config.hero_bg_pos_x != null ? Number(config.hero_bg_pos_x) : 50;
  const y = config.hero_bg_pos_y != null ? Number(config.hero_bg_pos_y) : 50;
  return `${isNaN(x) ? 50 : x}% ${isNaN(y) ? 50 : y}%`;
};

// background-size: 'cover' | 'contain' | 'NN%'.
export const heroBgSize = (config = {}) => {
  const modo = config.hero_bg_size || 'cover';
  if (modo !== 'porcentaje') return modo;
  const pct = config.hero_bg_size_pct != null ? Number(config.hero_bg_size_pct) : 100;
  return `${isNaN(pct) ? 100 : pct}%`;
};

// Shorthand completo para el `background` del hero. Con imagen aplica el
// overlay como gradiente plano encima; sin imagen, el degrade de la marca.
export const heroBackground = (config = {}, imgUrl = null) => {
  if (!imgUrl) {
    return `linear-gradient(135deg, ${config.color_primary || '#1a5f2a'} 0%, ${config.color_secondary || '#8B4513'} 100%)`;
  }
  const overlay = heroOverlayRgba(config);
  // La posicion y el tamanio van separados de la url: el shorthand
  // `position/size` exige que ambos vayan juntos y en ese orden.
  return `linear-gradient(${overlay}, ${overlay}), url(${imgUrl}) ${heroBgPosition(config)}/${heroBgSize(config)} no-repeat`;
};

// Posicion del texto dentro del hero: alineacion vertical + ajuste fino.
//
// OJO con el eje: en un flex `row` el eje vertical lo maneja alignItems, pero
// en `column` lo maneja justifyContent (y alignItems pasa a ser el horizontal).
// RematePage es row; ContactoPage y CondicionesPage son column. Por eso hay que
// decirle al helper con cual esta tratando, o se rompe el centrado horizontal.
export const heroTextLayout = (config = {}, direccion = 'row') => {
  const alineacion = config.hero_text_align_v || 'center';
  return direccion === 'column'
    ? { justifyContent: alineacion }
    : { alignItems: alineacion };
};

// El padding fino va en el ELEMENTO DEL TEXTO, no en el contenedor del hero.
// En RematePage el padding-top del contenedor lo maneja la CSS var
// --hero-padding-top (para que la media query mobile pueda pisarlo); un inline
// style ahi le ganaria al CSS y romperia el header transparente en mobile.
// `baseTop` / `baseBottom` son el padding que ya tenia el elemento: el ajuste
// del productor SE SUMA, no lo reemplaza (si no, poner el default en 0 borraria
// el padding que la pagina ya traia y cambiaria el aspecto de eventos que ya
// estan publicados).
//
// LOS DOS LADOS SE PASAN POR SEPARADO, y hasta ahora no. Habia un solo `base`
// que se sumaba arriba Y abajo, y eso alcanzo mientras el unico que lo usaba
// era `AnimalPage`, que pasa 24 y quiere 24 en los dos lados. Pero
// `CatalogoPage` le pasa el ALTO DE LA CABECERA —porque con cabecera
// transparente el hero pasa por detras y el texto tiene que EMPEZAR debajo de
// ella— y se comia hasta 120px de aire muerto al fondo del hero. `baseBottom`
// cae por default en `baseTop`, asi que los cinco llamadores que ya existian no
// cambian en nada.
export const heroTextPadding = (config = {}, baseTop = 0, baseBottom = baseTop) => ({
  paddingTop: baseTop + (Number(config.hero_text_padding_top) || 0),
  paddingBottom: baseBottom + (Number(config.hero_text_padding_bottom) || 0),
});

// Margen superior del hero zone (`--hero-margin-top`, lo consume
// `.micrositio-page .hero-zone` en index.css, con su tope de 60px en mobile).
// Automático: 0 si el hero es transparente (la cabecera flota encima) y el
// alto TEÓRICO de la cabecera si es sólida. Pero el alto real cambia con lo
// que el productor le pone a la cabecera (franja de bienvenida, dos filas) y
// el teórico no siempre coincide: `hero_margen_superior` (px) lo fija a mano
// desde Herramientas → Micrositio → Fondo del hero. null o vacío = automático,
// como siempre. Vive acá y no en cada página porque son CINCO las que lo
// setean: una regla copiada cinco veces se arregla en cuatro.
export const heroMargenSuperior = (config = {}, headerHeight = 0, heroTransparente = false) => {
  const crudo = config.hero_margen_superior;
  const manual = Number(crudo);
  if (crudo != null && crudo !== '' && Number.isFinite(manual) && manual >= 0) return manual;
  return heroTransparente ? 0 : headerHeight;
};
